import * as React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  formatDecimalForInput,
  parseDecimalInput,
  sanitizeDecimalTyping,
} from "@/lib/decimalInput";

/**
 * Campo para cantidades y dosis. Lo tecleado vive aquí como TEXTO («0,», «0,0»,
 * «0,05») y al formulario solo le llega el número (`null` si el campo está vacío
 * o todavía no es un número). Por qué: ver `src/lib/decimalInput.ts` (C2).
 *
 * Si el valor cambia desde fuera (otra fila, «restablecer», al abrir el
 * formulario), el texto se rehace; si el cambio es el eco de lo que se acaba de
 * teclear, no se toca: así «0,0» no se convierte en «» a mitad de escribir.
 */
export interface DecimalInputProps
  extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type" | "inputMode"> {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
}

export const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ value, onValueChange, onBlur, ...props }, ref) => {
    const { i18n } = useTranslation();
    const locale = i18n?.language;
    const [text, setText] = React.useState(() => formatDecimalForInput(value, locale));
    const textRef = React.useRef(text);
    textRef.current = text;

    React.useEffect(() => {
      const typed = parseDecimalInput(textRef.current);
      const incoming = value ?? null;
      const same =
        (typed ?? 0) === (incoming ?? 0) ||
        (typed != null && incoming != null && Math.abs(typed - incoming) < 1e-9);
      if (!same) setText(formatDecimalForInput(incoming, locale));
    }, [value, locale]);

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        onChange={(e) => {
          const cleaned = sanitizeDecimalTyping(e.target.value);
          setText(cleaned);
          onValueChange(parseDecimalInput(cleaned));
        }}
        onBlur={(e) => {
          // «0,» al salir del campo se queda en «0»: el separador suelto no dice nada.
          if (/[.,]$/.test(textRef.current)) setText(textRef.current.slice(0, -1));
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);
DecimalInput.displayName = "DecimalInput";
